/**
 * FormControlRenderers — button and input form controls.
 *
 * Extracts SharedButtonComponent and SharedInputComponent from
 * SharedComponentRenderer.tsx.
 *
 * Violations fixed in this file (Step 9 of the decomposition roadmap):
 *
 * Group B — interactions casts
 *   • (component as any).interactions (lines 2913, 2926) → component.interactions
 *   • (toggleData as any).accordionMode / .associatedContentId (lines 3206, 3211) →
 *     direct Record<string, unknown> index access + post-guard string assertion
 *
 * Group C — CSS any casts
 *   • buttonStyle: any (line 4201) → React.CSSProperties with borderFragment spread
 *   • properties.type as any (line 4404) → toHtmlInputType()
 *
 * Group E — sensor action casts
 *   • data.action as any (line 3190) → toSensorAction()
 *   • data.parameters as Record<string, any> (line 3191) → toSensorParameters()
 *
 * Additionally: React.ComponentType<any> for icon (line 2907) is replaced with
 * a fully-specified props interface.
 */
import React, { useMemo } from "react";
import {
  Component,
  ComponentType,
  Viewport,
  IconLibrary,
} from "@/types";
import type { ComponentInteraction, FormFieldMapping } from "@/types/interactions";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { iconLibraryService } from "@/services/icons/IconLibraryService";
import { useComponentInteraction } from "../InteractionContextRuntime";
import { liveInputValues, liveInputMeta } from "@/services/editor/live-input-values";
import { useEditorStore } from "@/stores/editor/editor-store";
import { useAuthStore } from "@/stores/auth/auth-store";
import { useDebugStore } from "@/stores/ai/debug-store";
import { useViewerStore } from "@/stores/viewer/viewer-store";
import { getBreakpointFromViewport } from "@/utils/breakpoint-utils";
import type { ResponsiveBreakpoint } from "@/stores/editor/responsive-store";
import { useFormInput } from "@/hooks/form/useFormInput";
import { MODAL_CLOSE_EVENT } from "../ContainerModalWrapper";
import { getRefetch } from "@/stores/data/refresh-store";
import { toast } from "sonner";
import { toHtmlInputType, toSensorAction, toSensorParameters } from "./utils";

export interface FormControlRendererProps {
  component: Component;
  viewport?: Viewport | ResponsiveBreakpoint;
}

// ─────────────────────────────────────────────────────────────────────────────
// Local helpers
// ─────────────────────────────────────────────────────────────────────────────

function isBlankFormValue(value: unknown): boolean {
  return value === null || value === undefined || value === "";
}

function getInputDisplayLabel(
  component: Component | undefined,
  fallbackField: string,
): string {
  const props = component?.properties || {};
  return (
    (typeof props.label === "string" && props.label) ||
    (typeof props.labelText === "string" && props.labelText) ||
    (typeof props.name === "string" && props.name) ||
    fallbackField
  );
}

function getRecordField(record: Record<string, unknown>, field: string): unknown {
  if (field in record) return record[field];
  const data = record.data;
  if (
    data &&
    typeof data === "object" &&
    !Array.isArray(data) &&
    field in (data as Record<string, unknown>)
  ) {
    return (data as Record<string, unknown>)[field];
  }
  return undefined;
}

/**
 * Runtime type guard for FormFieldMapping[].
 * Replaces all (formDataMapping as Array<{ schemaField?: string }>) casts.
 */
function isFormFieldMappingArray(value: unknown): value is FormFieldMapping[] {
  return (
    Array.isArray(value) &&
    value.every(
      (item): item is FormFieldMapping =>
        typeof item === "object" &&
        item !== null &&
        typeof (item as Record<string, unknown>).sourceComponentId === "string" &&
        typeof (item as Record<string, unknown>).schemaField === "string",
    )
  );
}

/**
 * Narrow a ComponentInteraction trigger type to check for click actions.
 * The runtime data layer may use 'click' (without 'on' prefix) in addition
 * to the typed 'onClick' value, so we accept both via string widening.
 */
function isClickTrigger(type: string): boolean {
  return type === "onClick" || type === "click";
}

// ─────────────────────────────────────────────────────────────────────────────
// ButtonRenderer (was SharedButtonComponent)
// ─────────────────────────────────────────────────────────────────────────────

type IconComponentType = React.ComponentType<{
  size?: number;
  color?: string;
  style?: React.CSSProperties;
}>;

const ButtonRenderer: React.FC<{
  component: Component;
  viewport?: Viewport | ResponsiveBreakpoint;
}> = React.memo(({ component, viewport = Viewport.DESKTOP }) => {
  const { style } = component;
  const editorComponents = useEditorStore((state) => state.components);
  const authUser = useAuthStore((state) => state.user);

  // Merge responsive breakpoint styles into properties
  const properties = useMemo(() => {
    const baseProps = component.properties || {};
    const breakpoint = getBreakpointFromViewport(viewport);
    const breakpointStyle =
      component.responsivePosition?.breakpoints?.[breakpoint]?.style;

    if (breakpointStyle) {
      return { ...baseProps, ...breakpointStyle };
    }
    return baseProps;
  }, [component.properties, component.responsivePosition, viewport]);

  const interaction = useComponentInteraction(component);
  const [isHovering, setIsHovering] = React.useState(false);
  const [isPressed, setIsPressed] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  // Replaces React.ComponentType<any> (Group C icon type fix)
  const [IconComponent, setIconComponent] =
    React.useState<IconComponentType | null>(null);
  const [_iconError, setIconError] = React.useState(false);

  // Resolve action config from properties OR declarative interactions[] array.
  // The Interaction Agent may set submit-form-data in either location.
  // Replaces (component as any).interactions cast (Group B, lines 2913 + 2926).
  const resolvedInteractionAction = useMemo(() => {
    if (typeof properties.action === "string") return null; // properties path takes priority
    const interactions: ComponentInteraction[] | undefined =
      component.interactions;
    if (!interactions?.length) return null;
    // Find a click-triggered interaction with a submit-form-data or toggle/navigate action
    for (const ci of interactions) {
      if (isClickTrigger(ci.trigger.type) && ci.actions.length) {
        return ci.actions[0];
      }
    }
    // Fallback: any interaction with actions
    return interactions[0]?.actions?.[0] ?? null;
    // component.interactions replaces (component as any).interactions in dep array
  }, [component.interactions, properties.action]);

  const action =
    typeof properties.action === "string"
      ? properties.action
      : resolvedInteractionAction?.type ?? undefined;

  // Typed formDataMapping replaces all downstream (formDataMapping as Array<{...}>) casts.
  const formDataMapping: FormFieldMapping[] = useMemo(() => {
    if (isFormFieldMappingArray(properties.formDataMapping)) {
      return properties.formDataMapping;
    }
    if (resolvedInteractionAction?.formDataMapping) {
      return resolvedInteractionAction.formDataMapping;
    }
    return [];
  }, [properties.formDataMapping, resolvedInteractionAction]);

  const submitMode =
    properties.submitMode === "upsert"
      ? "upsert"
      : resolvedInteractionAction?.submitMode === "upsert"
        ? "upsert"
        : "create";

  const isSubmitLikeButton =
    action === "submit-form-data" ||
    /submit/i.test(component.id) ||
    formDataMapping.length > 0 ||
    typeof properties.schemaId === "string" ||
    typeof properties.groupId === "string";

  React.useEffect(() => {
    if (!isSubmitLikeButton) return;
    const debug = useDebugStore.getState();
    if (!debug.isEnabled || !debug.planDebugData) return;
    const notFunctional = interaction.mode !== "functional";
    const wrongAction = action !== "submit-form-data";
    const notInteractive = !interaction.canInteract;
    const missingHandlers = !interaction.handlers;
    const reason = wrongAction
      ? `not-submit-form-data-action:${action || "missing-action"}`
      : notFunctional
        ? "non-functional-mode"
        : notInteractive
          ? "interaction-disabled"
          : missingHandlers
            ? "missing-interaction-handlers"
            : undefined;
    debug.recordRuntimeFormSubmitDiagnostic({
      componentId: component.id,
      status: reason ? "blocked" : "ready",
      reason,
      action,
      interactionMode: interaction.mode,
      canInteract: interaction.canInteract,
      hasInteractionHandlers: !!interaction.handlers,
      groupId:
        typeof properties.groupId === "string" ? properties.groupId : undefined,
      schemaId:
        typeof properties.schemaId === "string" ? properties.schemaId : undefined,
      submitMode,
      authUserPresent: !!authUser?.id,
      mappedFields: formDataMapping.map((m) => m.schemaField).filter(Boolean),
    });
  }, [
    action,
    authUser?.id,
    component.id,
    formDataMapping,
    interaction.canInteract,
    interaction.handlers,
    interaction.mode,
    isSubmitLikeButton,
    properties.groupId,
    properties.schemaId,
    submitMode,
  ]);

  // Get toggle state from viewer store - use selector for reactivity
  const toggleButtonActive = useViewerStore((state) => state.toggleButtonActive);
  const isToggleActive = useViewerStore((state) =>
    state.activeButtons.has(component.id),
  );

  // Load icon component dynamically when iconName is provided
  React.useEffect(() => {
    let mounted = true;

    const loadIcon = async () => {
      const iconName =
        typeof properties.iconName === "string" ? properties.iconName : null;
      // Support both 'library' and 'iconLibrary' property names
      const iconLibraryValue =
        typeof properties.iconLibrary === "string"
          ? properties.iconLibrary
          : typeof properties.library === "string"
            ? properties.library
            : "lucide";
      const iconLibrary =
        iconLibraryValue === "custom"
          ? IconLibrary.CUSTOM
          : iconLibraryValue === "radix"
            ? IconLibrary.RADIX
            : IconLibrary.LUCIDE;

      if (!iconName) {
        setIconComponent(null);
        setIconError(false);
        return;
      }

      try {
        const loadedIcon = await iconLibraryService.loadIcon(iconName, iconLibrary);
        if (mounted) {
          if (loadedIcon) {
            setIconComponent(() => loadedIcon as IconComponentType);
            setIconError(false);
          } else {
            setIconError(true);
            setIconComponent(null);
          }
        }
      } catch (err) {
        console.error(`Failed to load icon ${iconName}:`, err);
        if (mounted) {
          setIconError(true);
          setIconComponent(null);
        }
      }
    };

    loadIcon();

    return () => {
      mounted = false;
    };
  }, [properties.iconName, properties.iconLibrary, properties.library]);

  // Extract hover and active properties
  const hoverBgColor =
    typeof properties.hoverBackgroundColor === "string"
      ? properties.hoverBackgroundColor
      : undefined;
  const hoverTextColor =
    typeof properties.hoverColor === "string"
      ? properties.hoverColor
      : undefined;
  const hoverIconColorProp =
    typeof properties.hoverIconColor === "string"
      ? properties.hoverIconColor
      : undefined;

  const activeBgColor =
    typeof properties.activeBackgroundColor === "string"
      ? properties.activeBackgroundColor
      : undefined;
  const activeTextColor =
    typeof properties.activeColor === "string"
      ? properties.activeColor
      : undefined;
  const activeIconColorProp =
    typeof properties.activeIconColor === "string"
      ? properties.activeIconColor
      : undefined;

  // Determine active styles based on pressed/hover state (pressed > hover > base)
  const baseBackgroundColor =
    (typeof properties.backgroundColor === "string"
      ? properties.backgroundColor
      : undefined) ||
    (typeof style?.backgroundColor === "string" ? style.backgroundColor : undefined);

  const baseTextColor =
    (typeof properties.color === "string" ? properties.color : undefined) ||
    (typeof style?.color === "string" ? style.color : undefined);

  const baseIconColor =
    typeof properties.iconColor === "string" ? properties.iconColor : undefined;

  const currentBackgroundColor =
    isPressed && activeBgColor
      ? activeBgColor
      : isHovering && hoverBgColor
        ? hoverBgColor
        : baseBackgroundColor;

  const currentTextColor =
    isPressed && activeTextColor
      ? activeTextColor
      : isHovering && hoverTextColor
        ? hoverTextColor
        : baseTextColor;

  const currentIconColor =
    isToggleActive && activeIconColorProp
      ? activeIconColorProp
      : isPressed && activeIconColorProp
        ? activeIconColorProp
        : isHovering && hoverIconColorProp
          ? hoverIconColorProp
          : baseIconColor;

  // Handle button click in functional mode
  const handleClick = async () => {
    // Toggle button active state (for toggle buttons like notifications)
    toggleButtonActive(component.id);
    const target =
      typeof properties.target === "string" ? properties.target : undefined;
    const data = (properties.data || {}) as Record<string, unknown>;

    const recordSubmitClickBlock = (reason: string) => {
      if (!isSubmitLikeButton) return;
      const debug = useDebugStore.getState();
      if (!debug.isEnabled || !debug.planDebugData) return;
      debug.recordRuntimeFormSubmitDiagnostic({
        componentId: component.id,
        status: "blocked",
        reason,
        action,
        interactionMode: interaction.mode,
        canInteract: interaction.canInteract,
        hasInteractionHandlers: !!interaction.handlers,
        groupId:
          typeof properties.groupId === "string"
            ? properties.groupId
            : undefined,
        schemaId:
          typeof properties.schemaId === "string"
            ? properties.schemaId
            : undefined,
        submitMode,
        authUserPresent: !!authUser?.id,
        mappedFields: formDataMapping.map((m) => m.schemaField).filter(Boolean),
      });
    };

    // Debug logging for form submit troubleshooting
    console.log("[SharedButtonComponent] Click handler called", {
      componentId: component.id,
      canInteract: interaction.canInteract,
      hasHandlers: !!interaction.handlers,
      action,
      target,
      mode: interaction.mode,
    });

    // Allow submit-form-data and toggle-visibility to work in editor/viewing mode.
    const bypassActions = ["submit-form-data", "toggle-visibility"];
    if (
      (!interaction.canInteract || !interaction.handlers) &&
      !bypassActions.includes(action || "")
    ) {
      console.log("[SharedButtonComponent] Early return - cannot interact or no handlers");
      recordSubmitClickBlock(
        !interaction.canInteract
          ? "interaction-disabled"
          : "missing-interaction-handlers",
      );
      return;
    }

    if (isSubmitLikeButton && action !== "submit-form-data") {
      recordSubmitClickBlock(
        `not-submit-form-data-action:${action || "missing-action"}`,
      );
    }

    console.log("[SharedButtonComponent] Processing action:", {
      action,
      target,
      hasData: !!data,
    });

    switch (action) {
      case "navigate":
        if (interaction.handlers.onNavigate && target) {
          await interaction.handlers.onNavigate({
            targetPageId: target,
            parameters: data,
          });
        }
        break;

      case "navigate-project":
        if (interaction.handlers.onNavigateProject && target) {
          await interaction.handlers.onNavigateProject({
            targetProjectId: target,
            targetPageId:
              typeof data?.pageId === "string" ? data.pageId : undefined,
            openInNewTab:
              typeof data?.openInNewTab === "boolean"
                ? data.openInNewTab
                : false,
          });
        }
        break;

      case "api-call":
        if (interaction.handlers.onApiCall && target) {
          await interaction.handlers.onApiCall(target, data);
        }
        break;

      case "sensor-control":
        if (interaction.handlers.onSensorControl && target) {
          await interaction.handlers.onSensorControl({
            sensorId: target,
            // Replaces data.action as any (Group E, line 3190)
            action: toSensorAction(
              "action" in data ? data["action"] : undefined,
            ),
            // Replaces data.parameters as Record<string, any> (Group E, line 3191)
            parameters: toSensorParameters(
              "parameters" in data ? data["parameters"] : undefined,
            ),
          });
        }
        break;

      case "toggle-visibility": {
        // Resolve toggle target from properties.data OR declarative interactions[] array
        const toggleData =
          data && typeof data === "object" ? data : ({} as Record<string, unknown>);
        const toggleTargetIds: string[] =
          "targetIds" in toggleData && Array.isArray(toggleData.targetIds)
            ? toggleData.targetIds
            : resolvedInteractionAction?.targetComponentId
              ? [resolvedInteractionAction.targetComponentId]
              : [];
        if (interaction.handlers?.onToggleVisibility && toggleTargetIds.length > 0) {
          // Check for accordion mode - single-expansion logic.
          // Replaces (toggleData as any).accordionMode / .associatedContentId (Group B, lines 3206, 3211).
          if (
            "accordionMode" in toggleData &&
            toggleData["accordionMode"] === true &&
            "associatedContentId" in toggleData &&
            typeof toggleData["associatedContentId"] === "string"
          ) {
            const allAccordionContentIds: string[] = [];
            for (let i = 1; i <= 20; i++) {
              allAccordionContentIds.push(`accordion-content-${i}`);
            }
            // Sound cast: we just proved associatedContentId is string via typeof guard above
            interaction.handlers.onToggleVisibility(
              allAccordionContentIds,
              toggleData["associatedContentId"] as string,
            );
          } else {
            // Regular toggle-visibility mode
            interaction.handlers.onToggleVisibility(toggleTargetIds);
          }
        }
        break;
      }

      case "add-table-row":
        if (interaction.handlers.onAddTableRow && target) {
          interaction.handlers.onAddTableRow(target);
        }
        break;

      case "switch-tab":
        if (
          interaction.handlers.onToggleVisibility &&
          data &&
          typeof data === "object" &&
          "targetIds" in data &&
          Array.isArray(data.targetIds)
        ) {
          const allTabContentIds = data.targetIds;

          const buttonIdMatch = component.id.match(/tab-button-(.+)$/);
          const targetTabSuffix = buttonIdMatch ? buttonIdMatch[1] : null;
          const targetTabId = targetTabSuffix
            ? `tab-content-${targetTabSuffix}`
            : null;

          if (targetTabId && allTabContentIds.includes(targetTabId)) {
            interaction.handlers.onToggleVisibility(allTabContentIds, targetTabId);
          }
        }
        break;

      case "carousel-navigate":
        if (data && typeof data === "object") {
          const trackId =
            "trackId" in data && typeof data.trackId === "string"
              ? data.trackId
              : null;
          const direction =
            "direction" in data && typeof data.direction === "string"
              ? data.direction
              : null;
          const targetIndex =
            "index" in data && typeof data.index === "number"
              ? data.index
              : null;
          const dotIds =
            "dotIds" in data && Array.isArray(data.dotIds) ? data.dotIds : [];
          const itemCount =
            "itemCount" in data && typeof data.itemCount === "number"
              ? data.itemCount
              : 6;

          if (!trackId) {
            break;
          }

          const trackElement = document.querySelector(
            `[data-component-id="${trackId}"]`,
          );
          if (!trackElement) {
            break;
          }

          const scrollLeft = trackElement.scrollLeft;
          const scrollWidth = trackElement.scrollWidth;
          const itemWidth = scrollWidth / itemCount;

          let currentIndex = Math.round(scrollLeft / itemWidth);

          let newIndex = currentIndex;
          if (direction === "prev") {
            newIndex = Math.max(0, currentIndex - 1);
          } else if (direction === "next") {
            newIndex = Math.min(itemCount - 1, currentIndex + 1);
          } else if (
            targetIndex !== null &&
            targetIndex >= 0 &&
            targetIndex < itemCount
          ) {
            newIndex = targetIndex;
          }

          trackElement.scrollTo({
            left: newIndex * itemWidth,
            behavior: "smooth",
          });

          if (dotIds.length > 0) {
            dotIds.forEach((dotId: string, index: number) => {
              const dotElement = document
                .querySelector(`[data-component-id="${dotId}"]`)
                ?.querySelector("button");
              if (dotElement) {
                if (index === newIndex) {
                  dotElement.style.backgroundColor = "#10b981";
                  dotElement.style.opacity = "1";
                } else {
                  dotElement.style.backgroundColor = "#d1d5db";
                  dotElement.style.opacity = "0.6";
                }
              }
            });
          }
        }
        break;

      case "toggle-sidebar":
        if (data && typeof data === "object") {
          const targetId =
            "targetId" in data && typeof data.targetId === "string"
              ? data.targetId
              : null;
          const backdropId =
            "backdropId" in data && typeof data.backdropId === "string"
              ? data.backdropId
              : null;

          if (!targetId) {
            break;
          }

          const viewerStore = useViewerStore.getState();
          viewerStore.toggleComponent(targetId);
          const isToggled = viewerStore.isComponentToggled(targetId);

          const sidebarElement = document.querySelector(
            `[data-component-id="${targetId}"]`,
          );
          if (sidebarElement && sidebarElement instanceof HTMLElement) {
            if (isToggled) {
              sidebarElement.style.transform = "translateX(0)";
            } else {
              sidebarElement.style.transform = "translateX(-240px)";
            }
          }

          if (backdropId) {
            const backdropElement = document.querySelector(
              `[data-component-id="${backdropId}"]`,
            );
            if (backdropElement && backdropElement instanceof HTMLElement) {
              backdropElement.style.display = isToggled ? "block" : "none";
              backdropElement.style.opacity = isToggled ? "1" : "0";
            }
          }
        }
        break;

      case "swap-content":
        if (interaction.handlers.onSwapContent) {
          if (!target) {
            console.warn(
              "[SharedButtonComponent] swap-content: Missing target (containerId)",
            );
            break;
          }

          const targetContentId =
            typeof data?.targetContentId === "string"
              ? data.targetContentId
              : "";
          const transition =
            typeof data?.transition === "string"
              ? (data.transition as "fade" | "slide" | "instant")
              : undefined;

          if (targetContentId) {
            interaction.handlers.onSwapContent({
              containerId: target,
              targetContentId,
              transition,
            });
          } else {
            console.warn(
              "[SharedButtonComponent] swap-content: Missing targetContentId in data",
            );
          }
        }
        break;

      case "submit-form-data": {
        if (isSubmitting) break;
        setIsSubmitting(true);

        try {
          const { default: dataApi } = await import("@/services/api/dataApi");

          // Read submit config from properties, falling back to declarative interactions[] array
          const ria = resolvedInteractionAction;
          let groupId =
            typeof properties.groupId === "string"
              ? properties.groupId
              : ria?.groupId ?? undefined;
          let schemaId =
            typeof properties.schemaId === "string"
              ? properties.schemaId
              : ria?.schemaId ?? undefined;
          const mappedFormData = isFormFieldMappingArray(properties.formDataMapping)
            ? properties.formDataMapping
            : ria?.formDataMapping ?? [];

          // Runtime reconciliation: if the submit button's groupId or schemaId doesn't match
          // any data-bound component on the page, replace with the correct Data Discovery schema.
          // Declared outside the reconciliation block so computed-metrics can access it.
          const dataSchemaEntries: Array<{
            schemaId: string;
            groupId: string;
            fields: Set<string>;
          }> = [];
          if (groupId && schemaId) {
            const dataSchemaIds = new Set<string>();
            const dataGroupIds = new Set<string>();
            let scannedCount = 0;
            let dsFoundCount = 0;
            for (const comp of editorComponents) {
              scannedCount++;
              const props = comp.properties as Record<string, unknown> | undefined;
              const ds = props?.dataSource as Record<string, unknown> | undefined;
              const qd = ds?.queryDefinition as Record<string, unknown> | undefined;
              if (ds && !qd) {
                dsFoundCount++;
                console.log(
                  `[Button] Reconciliation scan: ${comp.id} (${comp.type}) has dataSource but no queryDefinition`,
                  {
                    dsKeys: Object.keys(ds),
                    dsType: ds.type,
                    hasQueryTemplateId: !!ds.queryTemplateId,
                  },
                );
              }
              if (qd?.schemaId && qd?.groupId) {
                dataGroupIds.add(qd.groupId as string);
                dataSchemaIds.add(qd.schemaId as string);
                let entry = dataSchemaEntries.find(
                  (e) => e.schemaId === qd.schemaId,
                );
                if (!entry) {
                  entry = {
                    schemaId: qd.schemaId as string,
                    groupId: qd.groupId as string,
                    fields: new Set(),
                  };
                  dataSchemaEntries.push(entry);
                }
                const measures = qd.measures as Array<{ field?: string }> | undefined;
                if (measures) {
                  for (const m of measures) {
                    if (m.field) entry.fields.add(m.field);
                  }
                }
                const groupBy = qd.groupBy as Array<{ field?: string }> | undefined;
                if (groupBy) {
                  for (const g of groupBy) {
                    if (g.field) entry.fields.add(g.field);
                  }
                }
              }
            }
            console.log(
              `[Button] Reconciliation scan summary: ${scannedCount} components scanned, ${dsFoundCount} have dataSource w/o queryDef, ${dataSchemaEntries.length} have queryDef with schema/group`,
              {
                buttonGroupId: groupId,
                buttonSchemaId: schemaId,
                dataGroupIds: [...dataGroupIds],
                dataSchemaIds: [...dataSchemaIds],
              },
            );

            const needsReconciliation =
              dataSchemaEntries.length > 0 &&
              (!dataGroupIds.has(groupId) || !dataSchemaIds.has(schemaId));
            if (needsReconciliation) {
              const formFields = mappedFormData
                .map((m) => m.schemaField)
                .filter(Boolean);
              const tokenize = (name: string) => {
                const n = name.replace(/([a-z])([A-Z])/g, "$1 $2").toLowerCase();
                return new Set(
                  n
                    .split(/[^a-z0-9]+/)
                    .filter(Boolean)
                    .map((t) =>
                      t.endsWith("ies")
                        ? t.slice(0, -3) + "y"
                        : t.endsWith("s") && t.length > 3
                          ? t.slice(0, -1)
                          : t,
                    ),
                );
              };
              const formTokenSets = formFields.map((f) => tokenize(f));

              type EnrichedEntry = {
                schemaId: string;
                groupId: string;
                allFields: Set<string>;
                schemaName: string;
              };
              const enrichedEntries: EnrichedEntry[] = [];
              let buttonNameTokens = new Set<string>();
              let buttonSchemaFieldTokens = new Set<string>();
              try {
                const [enriched, buttonSchema] = await Promise.all([
                  Promise.all(
                    dataSchemaEntries.map(
                      async (entry): Promise<EnrichedEntry> => {
                        try {
                          const schema = await dataApi.getAccessibleSchema(
                            entry.schemaId,
                          );
                          return {
                            ...entry,
                            allFields: new Set(
                              schema?.fields?.map(
                                (f: { name: string }) => f.name,
                              ) || [...entry.fields],
                            ),
                            schemaName: schema?.name || "",
                          };
                        } catch {
                          return {
                            ...entry,
                            allFields: entry.fields,
                            schemaName: "",
                          };
                        }
                      },
                    ),
                  ),
                  dataApi.getAccessibleSchema(schemaId).catch(() => null),
                ]);
                enrichedEntries.push(...enriched);
                if (buttonSchema?.name)
                  buttonNameTokens = tokenize(buttonSchema.name);
                if (buttonSchema?.fields) {
                  for (const f of buttonSchema.fields) {
                    for (const t of tokenize(f.name))
                      buttonSchemaFieldTokens.add(t);
                  }
                }
              } catch {
                enrichedEntries.push(
                  ...dataSchemaEntries.map((e) => ({
                    ...e,
                    allFields: e.fields,
                    schemaName: "",
                  })),
                );
              }

              let bestMatch: { schemaId: string; groupId: string } | null = null;
              let bestScore = 0;
              for (const entry of enrichedEntries) {
                const exactOverlap = formFields.filter((f) =>
                  entry.allFields.has(f),
                ).length;
                const schemaTokenSets = [...entry.allFields].map((f) =>
                  tokenize(f),
                );
                const allSchemaTokens = new Set<string>();
                for (const ts of schemaTokenSets) {
                  for (const t of ts) allSchemaTokens.add(t);
                }

                let tokenScore = 0;
                for (const formTokens of formTokenSets) {
                  let bestFieldScore = 0;
                  for (const schemaTokens of schemaTokenSets) {
                    const overlap = [...formTokens].filter((t) =>
                      schemaTokens.has(t),
                    ).length;
                    const score =
                      overlap / Math.max(formTokens.size, schemaTokens.size);
                    if (score > bestFieldScore) bestFieldScore = score;
                  }
                  tokenScore += bestFieldScore;
                }

                let nameBonus = 0;
                if (buttonNameTokens.size > 0 && entry.schemaName) {
                  const candidateTokens = tokenize(entry.schemaName);
                  const nameOverlap = [...buttonNameTokens].filter((t) =>
                    candidateTokens.has(t),
                  ).length;
                  nameBonus =
                    nameOverlap /
                    Math.max(buttonNameTokens.size, candidateTokens.size);
                }

                let contextBonus = 0;
                if (entry.schemaName) {
                  const schemaNameTokens = tokenize(entry.schemaName);
                  const buttonComp = editorComponents.find(
                    (c) => c.id === component.id,
                  );
                  const contextParts = [
                    component.id || "",
                    component.name || "",
                    (component.properties as Record<string, unknown>)
                      ?.label as string || "",
                    (component.properties as Record<string, unknown>)
                      ?.text as string || "",
                  ];
                  if (buttonComp?.parentId) {
                    const parent = editorComponents.find(
                      (c) => c.id === buttonComp.parentId,
                    );
                    if (parent)
                      contextParts.push(parent.name || "", parent.id || "");
                    if (parent?.parentId) {
                      const grandparent = editorComponents.find(
                        (c) => c.id === parent.parentId,
                      );
                      if (grandparent)
                        contextParts.push(
                          grandparent.name || "",
                          grandparent.id || "",
                        );
                    }
                  }
                  const contextTokens = new Set<string>();
                  for (const part of contextParts) {
                    for (const t of tokenize(part)) contextTokens.add(t);
                  }
                  for (const noise of [
                    "comp",
                    "submit",
                    "button",
                    "modal",
                    "form",
                    "container",
                    "flex",
                  ]) {
                    contextTokens.delete(noise);
                  }
                  if (contextTokens.size > 0) {
                    const overlap = [...contextTokens].filter((t) =>
                      schemaNameTokens.has(t),
                    ).length;
                    contextBonus = overlap > 0 ? overlap / contextTokens.size : 0;
                  }
                }
                const score =
                  exactOverlap * 2 +
                  tokenScore +
                  nameBonus * 3 +
                  contextBonus * 4;
                console.log(
                  `[Button] Reconciliation scoring: ${entry.schemaId} exact=${exactOverlap} token=${tokenScore.toFixed(2)} name=${nameBonus.toFixed(2)} context=${contextBonus.toFixed(2)} total=${score.toFixed(2)}`,
                );
                if (score > bestScore) {
                  bestScore = score;
                  bestMatch = entry;
                }
              }
              if (!bestMatch && enrichedEntries.length === 1) {
                bestMatch = enrichedEntries[0];
              }
              if (!bestMatch && enrichedEntries.length > 1) {
                let closestDiff = Infinity;
                for (const entry of enrichedEntries) {
                  const diff = Math.abs(
                    entry.allFields.size - formFields.length,
                  );
                  if (diff < closestDiff) {
                    closestDiff = diff;
                    bestMatch = entry;
                  }
                }
              }
              if (bestMatch) {
                console.log(
                  `[Button] Runtime schema reconciliation: group ${groupId} → ${bestMatch.groupId}, schema ${schemaId} → ${bestMatch.schemaId} (score: ${bestScore})`,
                );
                groupId = bestMatch.groupId;
                schemaId = bestMatch.schemaId;
              }
            }
          }

          const localSubmitMode =
            properties.submitMode === "upsert"
              ? "upsert"
              : ria?.submitMode === "upsert"
                ? "upsert"
                : "create";
          const riaMatchFields = ria?.matchFields;
          const matchFields = Array.isArray(properties.matchFields)
            ? properties.matchFields.filter(
                (field): field is string =>
                  typeof field === "string" && field.length > 0,
              )
            : Array.isArray(riaMatchFields)
              ? riaMatchFields.filter(
                  (field): field is string =>
                    typeof field === "string" && field.length > 0,
                )
              : [];
          const successMessage =
            typeof properties.successMessage === "string"
              ? properties.successMessage
              : typeof ria?.successMessage === "string"
                ? ria.successMessage
                : "Saved successfully";
          const errorMessage =
            typeof properties.errorMessage === "string"
              ? properties.errorMessage
              : typeof ria?.errorMessage === "string"
                ? ria.errorMessage
                : "Failed to save form";
          const closeOnSuccess =
            properties.closeOnSuccess !== undefined
              ? properties.closeOnSuccess !== false
              : ria?.closeOnSuccess !== undefined
                ? ria.closeOnSuccess !== false
                : true;

          const componentById = new Map(
            editorComponents.map((candidate) => [candidate.id, candidate]),
          );
          const recordSubmitDiagnostic = (diagnostic: {
            status: "started" | "blocked" | "success" | "error";
            reason?: string;
            mappedFields?: string[];
            payloadFields?: string[];
            refreshTargets?: string[];
            validationErrors?: string[];
            error?: string;
            hasUserId?: boolean;
            action?: string;
            interactionMode?: string;
            canInteract?: boolean;
            hasInteractionHandlers?: boolean;
            apiOperation?: "create" | "update";
            upsertMatched?: boolean;
            writtenRecordId?: string;
            apiResponseKeys?: string[];
            preSubmitRecordCount?: number;
            postSubmitRecordCount?: number;
            readBackFound?: boolean;
            readBackError?: string;
            schemaFetchError?: string;
            refreshResults?: Array<{
              targetId: string;
              method: "refetch" | "fallback-query";
              status: "success" | "error";
              error?: string;
            }>;
          }) => {
            const debug = useDebugStore.getState();
            if (!debug.isEnabled || !debug.planDebugData) return;
            debug.recordRuntimeFormSubmitDiagnostic({
              componentId: component.id,
              groupId,
              schemaId,
              submitMode: localSubmitMode,
              action,
              interactionMode: interaction.mode,
              canInteract: interaction.canInteract,
              hasInteractionHandlers: !!interaction.handlers,
              authUserPresent: !!authUser?.id,
              ...diagnostic,
            });
          };
          const shouldCaptureSubmitDebug = () => {
            const debug = useDebugStore.getState();
            return debug.isEnabled && !!debug.planDebugData;
          };

          if (!groupId || !schemaId || !mappedFormData.length) {
            console.warn(
              "[Button] submit-form-data missing required config",
              { groupId, schemaId, formDataMapping: mappedFormData },
            );
            recordSubmitDiagnostic({
              status: "blocked",
              reason: "missing-config",
              mappedFields: mappedFormData.map((m) => m.schemaField).filter(Boolean),
            });
            toast.error(errorMessage);
            break;
          }

          console.info("[Button] submit-form-data collecting values", {
            formDataMapping: mappedFormData,
            liveInputValuesKeys: Array.from(liveInputValues.keys()),
            liveInputValuesEntries: Array.from(liveInputValues.entries()),
          });
          const formData: Record<string, unknown> = {};
          for (const mapping of mappedFormData) {
            const value = liveInputValues.get(mapping.sourceComponentId);
            if (value !== undefined && value !== null) {
              formData[mapping.schemaField] = value;
            }
          }

          const explicitIds = new Set(mappedFormData.map((m) => m.sourceComponentId));
          for (const [componentId, value] of liveInputValues.entries()) {
            if (explicitIds.has(componentId)) continue;
            if (value === undefined || value === null) continue;
            const meta = liveInputMeta.get(componentId);
            const rawName = meta?.fieldName || meta?.name || meta?.label;
            const fieldName = rawName
              ? (() => {
                  const cleaned = String(rawName)
                    .replace(/\s*\([^)]*\)\s*/g, "")
                    .trim();
                  const words = cleaned.split(/\s+/);
                  return words
                    .map((w: string, i: number) =>
                      i === 0
                        ? w.toLowerCase()
                        : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase(),
                    )
                    .join("");
                })()
              : null;
            console.info("[Button] auto-map iteration", {
              componentId,
              value,
              hasMeta: !!meta,
              rawName,
              fieldName,
              alreadyInFormData: fieldName ? fieldName in formData : "N/A",
            });
            if (fieldName && !(fieldName in formData)) {
              formData[fieldName] = value;
            }
          }

          const validationErrors: string[] = [];
          let schemaHasUserIdField = false;
          let schemaFetchError: string | undefined;
          try {
            const schema = await dataApi.getAccessibleSchema(schemaId!);
            if (schema?.fields?.length) {
              const schemaFieldsByName = new Map(
                schema.fields.map((field) => [
                  field.name,
                  field,
                ]),
              );
              const schemaFieldNames = new Set(
                schema.fields.map((field) => field.name),
              );
              schemaHasUserIdField = schemaFieldNames.has("userId");

              const fieldRemapLookup = new Map<string, string>();
              const formKeys = Object.keys(formData);
              const unmatchedFormKeys = formKeys.filter(
                (k) => !schemaFieldNames.has(k),
              );
              if (unmatchedFormKeys.length > 0) {
                const unmatchedSchemaFields = schema.fields
                  .filter((field) => !formKeys.includes(field.name))
                  .map((field) => field.name);
                const normalize = (name: string) =>
                  name
                    .replace(/([a-z])([A-Z])/g, "$1_$2")
                    .toLowerCase()
                    .replace(/[^a-z0-9]/g, "");
                const tokenize = (name: string) => {
                  const n = name
                    .replace(/([a-z])([A-Z])/g, "$1 $2")
                    .toLowerCase();
                  return new Set(n.split(/[^a-z0-9]+/).filter(Boolean));
                };
                for (const formKey of unmatchedFormKeys) {
                  const formNorm = normalize(formKey);
                  const formTokens = tokenize(formKey);
                  let bestTarget: string | null = null;
                  let bestScore = 0;
                  for (const schemaField of unmatchedSchemaFields) {
                    const schemaNorm = normalize(schemaField);
                    const schemaTokens = tokenize(schemaField);
                    if (formNorm === schemaNorm) {
                      bestTarget = schemaField;
                      bestScore = 100;
                      break;
                    }
                    const overlap = [...formTokens].filter((t) =>
                      schemaTokens.has(t),
                    ).length;
                    const score =
                      overlap / Math.max(formTokens.size, schemaTokens.size);
                    if (score > bestScore && score >= 0.3) {
                      bestScore = score;
                      bestTarget = schemaField;
                    }
                  }
                  if (bestTarget) {
                    console.info(
                      `[Button] field remap: ${formKey} → ${bestTarget} (score: ${bestScore})`,
                    );
                    formData[bestTarget] = formData[formKey];
                    delete formData[formKey];
                    fieldRemapLookup.set(formKey, bestTarget);
                    const idx = unmatchedSchemaFields.indexOf(bestTarget);
                    if (idx >= 0) unmatchedSchemaFields.splice(idx, 1);
                  }
                }
              }

              for (const key of Object.keys(formData)) {
                if (!schemaFieldNames.has(key)) {
                  console.info("[Button] removing unknown field", key);
                  delete formData[key];
                }
              }

              const formMappedFields = new Set(
                mappedFormData
                  .map((m) => m.schemaField)
                  .filter(Boolean)
                  .flatMap((f) => {
                    const remapped = fieldRemapLookup.get(f!);
                    return remapped ? [f!, remapped] : [f!];
                  }),
              );
              const timestampFieldNames = new Set([
                "created_at",
                "updated_at",
                "start_date",
                "end_date",
                "created",
                "updated",
                "timestamp",
                "modified_at",
              ]);
              for (const field of schema.fields) {
                const nameLC = field.name.toLowerCase();
                const typeLC = (field.type || "").toLowerCase();
                const isDateLike =
                  typeLC === "date" ||
                  nameLC.includes("date") ||
                  nameLC.includes("time") ||
                  timestampFieldNames.has(nameLC);
                if (
                  !(field.name in formData) &&
                  !formMappedFields.has(field.name) &&
                  isDateLike
                ) {
                  const now = new Date();
                  const localISO = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}T${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}:${String(now.getSeconds()).padStart(2, "0")}`;
                  formData[field.name] = localISO;
                  console.info(
                    "[Button] auto-injected date field",
                    field.name,
                    localISO,
                    field.required ? "(required)" : "(optional, for query filtering)",
                  );
                }
                if (field.required && !(field.name in formData)) {
                  if (isDateLike) {
                    // Already handled above
                  } else if (!formMappedFields.has(field.name)) {
                    if (
                      typeLC === "number" ||
                      typeLC === "integer" ||
                      typeLC === "float" ||
                      typeLC === "double"
                    ) {
                      formData[field.name] = 0;
                      console.info(
                        "[Button] auto-injected default number for required field",
                        field.name,
                      );
                    } else if (typeLC === "boolean") {
                      formData[field.name] = false;
                      console.info(
                        "[Button] auto-injected default boolean for required field",
                        field.name,
                      );
                    } else {
                      const defaultsByName: Record<string, string> = {
                        unit: "lbs",
                        units: "lbs",
                        currency: "USD",
                        status: "active",
                        type: "default",
                        category: "general",
                        priority: "normal",
                        source: "web",
                      };
                      const defaultVal = defaultsByName[nameLC] || field.name;
                      formData[field.name] = defaultVal;
                      console.info(
                        "[Button] auto-injected default for required field",
                        field.name,
                        "=",
                        defaultVal,
                      );
                    }
                  }
                }
              }

              if (
                schemaFieldNames.has("userId") &&
                authUser?.id &&
                !("userId" in formData)
              ) {
                formData.userId = authUser.id;
              }

              for (const mapping of mappedFormData) {
                const sourceComponent = componentById.get(
                  mapping.sourceComponentId,
                );
                const sourceProps = sourceComponent?.properties || {};
                const label = getInputDisplayLabel(
                  sourceComponent,
                  mapping.schemaField,
                );
                const effectiveField =
                  fieldRemapLookup.get(mapping.schemaField) ||
                  mapping.schemaField;
                const value = formData[effectiveField];
                const required =
                  sourceProps.required === true ||
                  schemaFieldsByName.get(effectiveField)?.required === true;
                const minRaw = sourceProps.min;
                const minValue =
                  typeof minRaw === "number"
                    ? minRaw
                    : typeof minRaw === "string" &&
                        minRaw.trim() !== "" &&
                        !Number.isNaN(Number(minRaw))
                      ? Number(minRaw)
                      : undefined;
                const inputType =
                  typeof sourceProps.type === "string"
                    ? sourceProps.type
                    : undefined;

                if (required && isBlankFormValue(value)) {
                  validationErrors.push(`${label} is required`);
                  continue;
                }

                if (
                  !isBlankFormValue(value) &&
                  (inputType === "number" || minValue !== undefined)
                ) {
                  const numericValue = Number(value);
                  if (Number.isNaN(numericValue)) {
                    validationErrors.push(`${label} must be a number`);
                    continue;
                  }
                  if (minValue !== undefined && numericValue < minValue) {
                    validationErrors.push(
                      `${label} must be at least ${minValue}`,
                    );
                  }
                }
              }
            }
          } catch (schemaErr) {
            schemaFetchError =
              (schemaErr as Error).message || String(schemaErr);
            console.warn("[Button] schema fetch failed, submitting as-is", schemaErr);
          }

          if (validationErrors.length > 0) {
            recordSubmitDiagnostic({
              status: "blocked",
              reason: "validation",
              mappedFields: mappedFormData.map((m) => m.schemaField).filter(Boolean),
              payloadFields: Object.keys(formData),
              hasUserId: Object.prototype.hasOwnProperty.call(formData, "userId"),
              validationErrors,
              schemaFetchError,
            });
            toast.error(validationErrors[0]);
            break;
          }

          const upsertUsesUserIdMatch =
            localSubmitMode === "upsert" && matchFields.length === 0;
          const requiresAuthUser =
            schemaHasUserIdField ||
            matchFields.includes("userId") ||
            upsertUsesUserIdMatch ||
            Object.prototype.hasOwnProperty.call(formData, "userId");
          if (!authUser?.id && requiresAuthUser) {
            recordSubmitDiagnostic({
              status: "blocked",
              reason: "auth",
              mappedFields: mappedFormData.map((m) => m.schemaField).filter(Boolean),
              payloadFields: Object.keys(formData),
              hasUserId: Object.prototype.hasOwnProperty.call(formData, "userId"),
              schemaFetchError,
            });
            toast.error("Please sign in to continue");
            break;
          }

          const riaRefreshTargets = ria?.refreshTargets;
          const refreshTargets = Array.isArray(properties.refreshTargets)
            ? properties.refreshTargets
            : Array.isArray(riaRefreshTargets)
              ? riaRefreshTargets
              : [];
          recordSubmitDiagnostic({
            status: "started",
            mappedFields: mappedFormData.map((m) => m.schemaField).filter(Boolean),
            payloadFields: Object.keys(formData),
            hasUserId: Object.prototype.hasOwnProperty.call(formData, "userId"),
            refreshTargets: refreshTargets.filter(
              (t): t is string => typeof t === "string",
            ),
            schemaFetchError,
          });
          console.info("[Button] submit-form-data payload", {
            formData,
            fieldCount: Object.keys(formData).length,
          });

          let apiOperation: "create" | "update" = "create";
          let upsertMatched = false;
          let writtenRecordId: string | undefined;
          let apiResponseKeys: string[] | undefined;
          let preSubmitRecordCount: number | undefined;
          let postSubmitRecordCount: number | undefined;
          let readBackFound: boolean | undefined;
          let readBackError: string | undefined;

          if (localSubmitMode === "upsert") {
            const { records } = await dataApi.queryRecords(groupId, schemaId, {
              limit: 100,
            });
            preSubmitRecordCount = records.length;
            const upsertKeys =
              matchFields.length > 0 ? matchFields : ["userId"];
            const existing = records.find((record: Record<string, unknown>) =>
              upsertKeys.every((field) => {
                const recordValue = getRecordField(record, field);
                const formValue = formData[field];
                return (
                  !isBlankFormValue(formValue) &&
                  String(recordValue ?? "") === String(formValue ?? "")
                );
              }),
            );
            const recordId =
              typeof existing?._id === "string"
                ? existing._id
                : typeof existing?.id === "string"
                  ? existing.id
                  : null;

            if (recordId) {
              apiOperation = "update";
              upsertMatched = true;
              const updateResult = await dataApi.updateRecord(
                groupId,
                schemaId,
                recordId,
                formData,
              );
              writtenRecordId = recordId;
              apiResponseKeys = Object.keys(updateResult || {});
            } else {
              apiOperation = "create";
              upsertMatched = false;
              const createResult = await dataApi.createRecord(
                groupId,
                schemaId,
                formData,
                {
                  source: "form-interaction",
                  tags: ["user-submitted"],
                },
              );
              writtenRecordId =
                typeof createResult?.insertedId === "string"
                  ? createResult.insertedId
                  : undefined;
              apiResponseKeys = Object.keys(createResult || {});
            }
          } else {
            apiOperation = "create";
            if (shouldCaptureSubmitDebug()) {
              try {
                const before = await dataApi.queryRecords(groupId, schemaId, {
                  limit: 100,
                });
                preSubmitRecordCount = before.records.length;
              } catch (readErr) {
                readBackError = `pre-submit read failed: ${(readErr as Error).message || String(readErr)}`;
              }
            }
            const createResult = await dataApi.createRecord(
              groupId,
              schemaId,
              formData,
              {
                source: "form-interaction",
                tags: ["user-submitted"],
              },
            );
            writtenRecordId =
              typeof createResult?.insertedId === "string"
                ? createResult.insertedId
                : undefined;
            apiResponseKeys = Object.keys(createResult || {});
          }

          if (shouldCaptureSubmitDebug()) {
            try {
              const after = await dataApi.queryRecords(groupId, schemaId, {
                limit: 100,
              });
              postSubmitRecordCount = after.records.length;
              readBackFound = writtenRecordId
                ? after.records.some(
                    (record: Record<string, unknown>) =>
                      String(record._id ?? record.id ?? "") === writtenRecordId,
                  )
                : postSubmitRecordCount > (preSubmitRecordCount ?? -1);
            } catch (readErr) {
              readBackError =
                readBackError ||
                `post-submit read failed: ${(readErr as Error).message || String(readErr)}`;
            }
          }

          const refreshResults: Array<{
            targetId: string;
            method: "refetch" | "fallback-query";
            status: "success" | "error";
            error?: string;
          }> = [];

          // Clear all form inputs after successful submission
          for (const cid of [...explicitIds, ...liveInputValues.keys()]) {
            liveInputValues.set(cid, "");
          }

          if (refreshTargets.length) {
            const { useComponentDataStore } = await import(
              "@/stores/editor/component-data-store"
            );
            for (const targetId of refreshTargets) {
              if (typeof targetId === "string") {
                const refetch = getRefetch(targetId);
                if (refetch) {
                  try {
                    await refetch();
                    refreshResults.push({
                      targetId,
                      method: "refetch",
                      status: "success",
                    });
                  } catch (refreshErr) {
                    refreshResults.push({
                      targetId,
                      method: "refetch",
                      status: "error",
                      error:
                        (refreshErr as Error).message || String(refreshErr),
                    });
                  }
                  continue;
                }

                const targetComponent = componentById.get(targetId);
                const targetProps = targetComponent?.properties || {};
                const targetDataSource = targetProps.dataSource as
                  | Record<string, unknown>
                  | undefined;
                const targetHasQueryTemplate =
                  typeof targetProps.queryTemplateId === "string" ||
                  targetDataSource?.type === "query-template";
                if (targetHasQueryTemplate) {
                  refreshResults.push({
                    targetId,
                    method: "refetch",
                    status: "error",
                    error: "no registered query-template refetch for target",
                  });
                  continue;
                }

                useComponentDataStore.getState().setLoading(targetId, true);
                try {
                  const { records } = await dataApi.queryRecords(
                    groupId,
                    schemaId,
                    { limit: 100 },
                  );
                  useComponentDataStore.getState().setData(targetId, records);
                  refreshResults.push({
                    targetId,
                    method: "fallback-query",
                    status: "success",
                  });
                } catch (refreshErr) {
                  const refreshMessage =
                    (refreshErr as Error).message || "Failed to refresh data";
                  useComponentDataStore.getState().setError(targetId, refreshMessage);
                  refreshResults.push({
                    targetId,
                    method: "fallback-query",
                    status: "error",
                    error: refreshMessage,
                  });
                }
              }
            }
          }

          // Computed metrics: update KPIs that need cross-schema data (e.g. percent to goal, days to goal).
          // This runs after all individual query template refetches so that underlying data is fresh.
          if (dataSchemaEntries.length >= 2) {
            try {
              // Identify entries vs goals schemas by their field names.
              // "entries" schemas have fields like weight/date/notes.
              // "goals" schemas have fields like target_weight/target_date/start_weight.
              const goalsSchema = dataSchemaEntries.find(
                (e) =>
                  e.fields.has("target_weight") ||
                  e.fields.has("goal_weight") ||
                  e.fields.has("goalWeight"),
              );
              const entriesSchema = dataSchemaEntries.find(
                (e) =>
                  e !== goalsSchema &&
                  (e.fields.has("weight") || e.fields.has("date")),
              );
              if (entriesSchema && goalsSchema) {
                const metricsResult = await dataApi.computeMetrics({
                  groupId,
                  metrics: [
                    "percentToGoal",
                    "daysToGoal",
                    "currentValue",
                    "goalValue",
                    "changeFromStart",
                    "remainingToGoal",
                  ],
                  schemas: {
                    entries: entriesSchema.schemaId,
                    goals: goalsSchema.schemaId,
                  },
                });
                console.info("[Button] computed metrics result", metricsResult);
                const { useComponentDataStore: getCompDataStore } =
                  await import("@/stores/editor/component-data-store");
                const kpiMetricMap: Record<string, string> = {
                  percent: "percentToGoal",
                  progress: "percentToGoal",
                  "days to goal": "daysToGoal",
                  "days remaining": "daysToGoal",
                  remaining: "remainingToGoal",
                  change: "changeFromStart",
                };
                for (const [, comp] of componentById) {
                  const props = comp.properties || {};
                  const title = String(
                    (props as Record<string, unknown>).title ||
                      (props as Record<string, unknown>).label ||
                      (props as Record<string, unknown>).name ||
                      comp.name ||
                      "",
                  ).toLowerCase();
                  for (const [keyword, metricKey] of Object.entries(
                    kpiMetricMap,
                  )) {
                    if (
                      title.includes(keyword) &&
                      (metricsResult as Record<string, unknown>)[metricKey] !=
                        null
                    ) {
                      getCompDataStore
                        .getState()
                        .setData(comp.id, [
                          {
                            value: (
                              metricsResult as Record<string, unknown>
                            )[metricKey],
                          },
                        ]);
                      console.info(
                        `[Button] injected computed metric ${metricKey}=${(metricsResult as Record<string, unknown>)[metricKey]} into ${comp.id} (${title})`,
                      );
                      break;
                    }
                  }
                }
              }
            } catch (metricsErr) {
              console.warn(
                "[Button] computed metrics failed (non-blocking)",
                metricsErr,
              );
            }
          }

          if (
            closeOnSuccess &&
            component.parentId &&
            typeof window !== "undefined"
          ) {
            window.dispatchEvent(
              new CustomEvent(MODAL_CLOSE_EVENT, {
                detail: { componentId: component.parentId },
              }),
            );
          }

          toast.success(successMessage);
          recordSubmitDiagnostic({
            status: "success",
            mappedFields: mappedFormData.map((m) => m.schemaField).filter(Boolean),
            payloadFields: Object.keys(formData),
            hasUserId: Object.prototype.hasOwnProperty.call(formData, "userId"),
            refreshTargets: refreshTargets.filter(
              (t): t is string => typeof t === "string",
            ),
            apiOperation,
            upsertMatched,
            writtenRecordId,
            apiResponseKeys,
            preSubmitRecordCount,
            postSubmitRecordCount,
            readBackFound,
            readBackError,
            schemaFetchError,
            refreshResults,
          });
          console.log("[Button] Record saved successfully");
        } catch (error) {
          console.error("[Button] submit-form-data failed", error);
          const configuredMessage =
            typeof properties.errorMessage === "string"
              ? properties.errorMessage
              : undefined;
          const message =
            (error as Error)?.message ||
            configuredMessage ||
            "Failed to save form";
          const debug = useDebugStore.getState();
          if (debug.isEnabled && debug.planDebugData) {
            debug.recordRuntimeFormSubmitDiagnostic({
              componentId: component.id,
              status: "error",
              reason: "exception",
              groupId:
                typeof properties.groupId === "string"
                  ? properties.groupId
                  : undefined,
              schemaId:
                typeof properties.schemaId === "string"
                  ? properties.schemaId
                  : undefined,
              submitMode:
                properties.submitMode === "upsert" ? "upsert" : "create",
              authUserPresent: !!authUser?.id,
              error: message,
            });
          }
          toast.error(
            message === "UNKNOWN_ERROR"
              ? configuredMessage || "Failed to save form"
              : message,
          );
        } finally {
          setIsSubmitting(false);
        }
        break;
      }

      case "form-submit":
        if (interaction.handlers.onFormSubmit) {
          let formContainerId = target;

          if (!formContainerId) {
            if (component.parentId && component.parentId !== "root") {
              const parentElement = document.querySelector(
                `[data-form-id="${component.parentId}"]`,
              );
              if (parentElement) {
                formContainerId = component.parentId;
                console.log(
                  "[SharedButtonComponent] form-submit: found form via parentId:",
                  formContainerId,
                );
              }
            }

            if (!formContainerId) {
              const componentWrapper = document.querySelector(
                `[data-component-id="${component.id}"]`,
              );
              if (componentWrapper) {
                const allForms = Array.from(
                  document.querySelectorAll("[data-form-id]"),
                );
                const allWrappers = Array.from(
                  document.querySelectorAll("[data-component-id]"),
                );
                const buttonIndex = allWrappers.indexOf(
                  componentWrapper as Element,
                );

                for (let i = allForms.length - 1; i >= 0; i--) {
                  const formEl = allForms[i];
                  const formWrapper =
                    formEl.closest("[data-component-id]") || formEl;
                  const formIndex = allWrappers.indexOf(
                    formWrapper as Element,
                  );
                  if (formIndex < buttonIndex && formIndex !== -1) {
                    formContainerId =
                      formEl.getAttribute("data-form-id") || undefined;
                    console.log(
                      "[SharedButtonComponent] form-submit: found preceding form:",
                      formContainerId,
                    );
                    break;
                  }
                }
              }
            }
          }

          if (formContainerId) {
            console.log(
              "[SharedButtonComponent] Calling onFormSubmit with formContainerId:",
              formContainerId,
            );
            await interaction.handlers.onFormSubmit({
              formContainerId,
              submitData: data,
            });
          } else {
            console.warn(
              "[SharedButtonComponent] form-submit: Could not find form container",
            );
          }
        }
        break;
    }
  };

  // ── Style computation ───────────────────────────────────────────────────────

  const completeBorder =
    typeof properties.border === "string" ? properties.border : undefined;

  const borderWidth =
    (typeof properties.borderWidth === "string"
      ? properties.borderWidth
      : undefined) ||
    (typeof style?.borderWidth === "string" ? style.borderWidth : undefined);

  const borderColor =
    (typeof properties.borderColor === "string"
      ? properties.borderColor
      : undefined) ||
    (typeof style?.borderColor === "string" ? style.borderColor : undefined);

  // Renamed from 'borderStyle' to avoid conflict with the React.CSSProperties key.
  const borderStyleValue =
    (typeof properties.borderStyle === "string"
      ? properties.borderStyle
      : undefined) ||
    (typeof style?.borderStyle === "string" ? style.borderStyle : undefined);

  const filteredStyle = style ? { ...style } : {};
  delete filteredStyle.border;
  delete filteredStyle.borderWidth;
  delete filteredStyle.borderColor;
  delete filteredStyle.borderStyle;
  delete filteredStyle.borderRadius;
  delete filteredStyle.boxShadow;
  delete filteredStyle.backgroundColor;

  // Compute border fragment upfront so buttonStyle can be typed as React.CSSProperties.
  // Replaces the `buttonStyle: any` pattern (Group C, line 4201).
  const borderFragment = ((): React.CSSProperties => {
    if (completeBorder) {
      const parts = completeBorder.split(" ");
      if (parts.length >= 3) {
        return {
          borderWidth: parts[0],
          borderStyle: parts[1],
          borderColor: parts.slice(2).join(" "),
        };
      }
      return { border: completeBorder };
    }
    if (borderWidth || borderColor || borderStyleValue) {
      return {
        borderWidth,
        borderColor,
        borderStyle: borderStyleValue,
      };
    }
    return {};
  })();

  const buttonStyle: React.CSSProperties = {
    ...filteredStyle,
    ...borderFragment,
    backgroundColor: currentBackgroundColor,
    color: currentTextColor,
    fontSize:
      (typeof properties.fontSize === "string" ||
      typeof properties.fontSize === "number"
        ? properties.fontSize
        : undefined) ||
      (typeof style?.fontSize === "string" || typeof style?.fontSize === "number"
        ? style.fontSize
        : undefined),
    fontWeight:
      (typeof properties.fontWeight === "string" ||
      typeof properties.fontWeight === "number"
        ? properties.fontWeight
        : undefined) ||
      (typeof style?.fontWeight === "string" ||
      typeof style?.fontWeight === "number"
        ? style.fontWeight
        : undefined),
    borderRadius:
      (typeof properties.borderRadius === "string" ||
      typeof properties.borderRadius === "number"
        ? properties.borderRadius
        : undefined) ||
      (typeof style?.borderRadius === "string" ||
      typeof style?.borderRadius === "number"
        ? style.borderRadius
        : undefined),
    boxShadow:
      (typeof properties.boxShadow === "string"
        ? properties.boxShadow
        : undefined) ||
      (typeof style?.boxShadow === "string" ? style.boxShadow : undefined),
    textAlign:
      typeof properties.textAlign === "string"
        ? (properties.textAlign as React.CSSProperties["textAlign"])
        : undefined,
    justifyContent:
      typeof properties.justifyContent === "string"
        ? (properties.justifyContent as React.CSSProperties["justifyContent"])
        : undefined,
    display: "flex",
    alignItems: "center",
    gap: IconComponent
      ? typeof properties.iconGap === "number"
        ? `${properties.iconGap}px`
        : typeof properties.iconGap === "string"
          ? properties.iconGap
          : "8px"
      : undefined,
    width: "100%",
    height: "100%",
    minWidth:
      typeof properties.minWidth === "string" ||
      typeof properties.minWidth === "number"
        ? properties.minWidth
        : undefined,
    minHeight:
      typeof properties.minHeight === "string" ||
      typeof properties.minHeight === "number"
        ? properties.minHeight
        : undefined,
    padding:
      typeof properties.padding === "string" ||
      typeof properties.padding === "number"
        ? properties.padding
        : undefined,
    transition:
      "background-color 200ms ease, color 200ms ease, border 200ms ease, box-shadow 200ms ease",
  };

  const variantValue =
    properties.variant &&
    typeof properties.variant === "string" &&
    (["default", "destructive", "outline", "secondary", "ghost", "link"] as const).includes(
      properties.variant as "default" | "destructive" | "outline" | "secondary" | "ghost" | "link",
    )
      ? (properties.variant as "default" | "destructive" | "outline" | "secondary" | "ghost" | "link")
      : "default";

  const sizeValue =
    properties.size &&
    typeof properties.size === "string" &&
    (["default", "sm", "lg", "icon"] as const).includes(
      properties.size as "default" | "sm" | "lg" | "icon",
    )
      ? (properties.size as "default" | "sm" | "lg" | "icon")
      : "default";

  return (
    <Button
      variant={variantValue}
      size={sizeValue}
      disabled={properties.disabled === true}
      onClick={interaction.mode === "functional" ? handleClick : undefined}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => {
        setIsHovering(false);
        setIsPressed(false);
      }}
      onMouseDown={() => setIsPressed(true)}
      onMouseUp={() => setIsPressed(false)}
      onTouchStart={() => setIsPressed(true)}
      onTouchEnd={() => setIsPressed(false)}
      style={buttonStyle}
    >
      {/* Render icon on left if position is left (default) */}
      {IconComponent && properties.iconPosition !== "right" && (
        <IconComponent
          size={
            typeof properties.iconSize === "number" ? properties.iconSize : 16
          }
          color={currentIconColor || currentTextColor}
          style={{ flexShrink: 0 }}
        />
      )}

      {/* Render button text - support both 'label' and 'text' properties */}
      {typeof properties.label === "string"
        ? properties.label
        : typeof properties.text === "string"
          ? properties.text
          : "Button"}

      {/* Render icon on right if position is right */}
      {IconComponent && properties.iconPosition === "right" && (
        <IconComponent
          size={
            typeof properties.iconSize === "number" ? properties.iconSize : 16
          }
          color={currentIconColor || currentTextColor}
          style={{ flexShrink: 0 }}
        />
      )}
    </Button>
  );
});

ButtonRenderer.displayName = "ButtonRenderer";

// ─────────────────────────────────────────────────────────────────────────────
// InputRenderer (was SharedInputComponent)
// ─────────────────────────────────────────────────────────────────────────────

const InputRenderer: React.FC<{ component: Component }> = React.memo(
  ({ component }) => {
    const { properties = {}, style, size } = component;
    const interaction = useComponentInteraction(component);

    const fieldName =
      typeof properties.fieldName === "string"
        ? properties.fieldName
        : undefined;

    const formInput = useFormInput({ fieldName });

    const [localValue, setLocalValue] = React.useState(
      typeof properties.defaultValue === "string"
        ? properties.defaultValue
        : "",
    );

    // Seed liveInputValues on mount so submit-form-data sees default values
    React.useEffect(() => {
      const initialValue =
        typeof properties.defaultValue === "string"
          ? properties.defaultValue
          : "";
      if (initialValue || liveInputValues.get(component.id) === undefined) {
        liveInputValues.set(component.id, initialValue);
      }
      liveInputMeta.set(component.id, {
        fieldName:
          typeof properties.fieldName === "string"
            ? properties.fieldName
            : undefined,
        name:
          typeof properties.name === "string" ? properties.name : undefined,
        label:
          typeof properties.label === "string"
            ? properties.label
            : typeof properties.labelText === "string"
              ? properties.labelText
              : undefined,
      });
      return () => {
        liveInputValues.delete(component.id);
        liveInputMeta.delete(component.id);
      };
    }, [
      component.id,
      properties.defaultValue,
      properties.fieldName,
      properties.name,
      properties.label,
      properties.labelText,
    ]);

    const currentValue = formInput.isInForm
      ? ((formInput.value as string) || "")
      : localValue;
    const handleChange = formInput.isInForm
      ? formInput.handleChange
      : setLocalValue;

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (interaction.mode === "functional" && interaction.canInteract) {
        handleChange(e.target.value);
        liveInputValues.set(component.id, e.target.value);
      }
    };

    const handleTextareaChange = (
      e: React.ChangeEvent<HTMLTextAreaElement>,
    ) => {
      if (interaction.mode === "functional" && interaction.canInteract) {
        handleChange(e.target.value);
        liveInputValues.set(component.id, e.target.value);
      }
    };

    const handleBlur = () => {
      if (formInput.isInForm && formInput.handleBlur) {
        formInput.handleBlur();
      }
    };

    const isTextarea = properties.type === "textarea";

    const labelText =
      typeof properties.labelText === "string"
        ? properties.labelText
        : typeof properties.label === "string"
          ? properties.label
          : fieldName;
    const showLabel = properties.showLabel !== false && !!labelText;
    const isRequired = properties.required === true;

    const commonStyles: React.CSSProperties = {
      ...style,
      fontSize:
        (typeof properties.fontSize === "string" ||
        typeof properties.fontSize === "number"
          ? properties.fontSize
          : undefined) ||
        (typeof style?.fontSize === "string" ||
        typeof style?.fontSize === "number"
          ? style.fontSize
          : undefined),
      color:
        (typeof properties.color === "string"
          ? properties.color
          : undefined) ||
        (typeof style?.color === "string" ? style.color : undefined),
      backgroundColor:
        (typeof properties.backgroundColor === "string"
          ? properties.backgroundColor
          : undefined) ||
        (typeof style?.backgroundColor === "string"
          ? style.backgroundColor
          : undefined),
      borderColor: formInput.error
        ? "#ef4444"
        : (typeof properties.borderColor === "string"
            ? properties.borderColor
            : undefined) ||
          (typeof style?.borderColor === "string"
            ? style.borderColor
            : undefined),
    };

    const textareaStyles: React.CSSProperties = isTextarea
      ? {
          ...commonStyles,
          minHeight:
            typeof properties.minHeight === "number" ||
            typeof properties.minHeight === "string"
              ? properties.minHeight
              : size?.height || 80,
          maxHeight:
            typeof properties.maxHeight === "number" ||
            typeof properties.maxHeight === "string"
              ? properties.maxHeight
              : undefined,
          height: size?.height || "100%",
          resize: "vertical" as React.CSSProperties["resize"],
        }
      : commonStyles;

    return (
      <div className="w-full h-full flex flex-col">
        {/* Label */}
        {showLabel && (
          <label className="text-sm font-medium text-gray-700 mb-1">
            {labelText}
            {isRequired && <span className="text-red-500 ml-1">*</span>}
          </label>
        )}

        {/* Input */}
        <div className="flex-1 flex items-center">
          {isTextarea ? (
            <Textarea
              placeholder={
                typeof properties.placeholder === "string"
                  ? properties.placeholder
                  : ""
              }
              value={currentValue}
              onChange={handleTextareaChange}
              onBlur={handleBlur}
              disabled={
                interaction.mode !== "functional" || !interaction.canInteract
              }
              style={textareaStyles}
              className={cn("w-full", formInput.error && "border-red-500")}
              data-field-name={fieldName}
            />
          ) : (
            <Input
              // Replaces `properties.type as any` (Group C, line 4404)
              type={toHtmlInputType(properties.type)}
              placeholder={
                typeof properties.placeholder === "string"
                  ? properties.placeholder
                  : ""
              }
              value={currentValue}
              onChange={handleInputChange}
              onBlur={handleBlur}
              disabled={
                interaction.mode !== "functional" || !interaction.canInteract
              }
              style={commonStyles}
              className={cn(formInput.error && "border-red-500")}
              data-field-name={fieldName}
            />
          )}
        </div>

        {/* Error message */}
        {typeof formInput.error === "string" && (
          <span className="text-xs text-red-500 mt-1">{formInput.error}</span>
        )}
      </div>
    );
  },
);

InputRenderer.displayName = "InputRenderer";

// ─────────────────────────────────────────────────────────────────────────────
// Public dispatcher
// ─────────────────────────────────────────────────────────────────────────────

export function FormControlRenderers({
  component,
  viewport,
}: FormControlRendererProps): JSX.Element {
  if (component.type === ComponentType.BUTTON) {
    return <ButtonRenderer component={component} viewport={viewport} />;
  }
  // ComponentType.INPUT
  return <InputRenderer component={component} />;
}
